import type { Lesson } from '../schema.js';

// Mini-Bridge lessons: explain + quiz only (no chess FEN steps), like backgammon's. The facts match
// the game's rules (docs/proposals/mindgames-and-bridge.md). They deliberately do NOT quote the
// contract table: it is calibrated and awaiting a bridge teacher's review, so the app tells the
// player the number instead.
export const BRIDGE_LESSONS: Lesson[] = [
  {
    id: 'bridge-points',
    slug: 'bridge-counting-points',
    title: 'Counting your points',
    audienceLevel: 'beginner',
    concepts: ['bridge'],
    steps: [
      {
        kind: 'explain',
        title: 'Face cards are worth points',
        body: 'Before you play, count your hand. An Ace is worth 4 points, a King 3, a Queen 2 and a Jack 1. Every other card is worth nothing. The whole deck holds 40 points, so an average hand has 10. Add your points to your partner\'s and you know how strong your side is: more points together means you can promise to win more tricks.',
        narration: 'Ace is four, King is three, Queen is two, Jack is one. Count them up!',
      },
      {
        kind: 'quiz',
        title: 'Count the points',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'How many points is an Ace worth?',
            choices: ['1', '4', '10'],
            correct: 1,
          },
          {
            prompt: 'Your hand has one Ace, one King and one Jack. How many points is that?',
            choices: ['6', '7', '8'],
            correct: 2,
          },
          {
            prompt: 'A seven of hearts is worth...',
            choices: ['7 points', '1 point', 'no points'],
            correct: 2,
          },
        ],
      },
    ],
  },
  {
    id: 'bridge-tricks',
    slug: 'bridge-tricks',
    title: 'What is a trick?',
    audienceLevel: 'beginner',
    concepts: ['bridge'],
    steps: [
      {
        kind: 'explain',
        title: 'Four cards, one winner',
        body: 'Each player puts one card on the table. Those four cards are a trick, and the player with the winning card takes it. Whoever wins the trick leads the next one. Everyone has 13 cards, so there are 13 tricks in a hand. The side that promised to win a certain number of tricks is the declaring side; the other side tries to stop them.',
        narration: 'Four cards make a trick. Win it, and you lead the next one.',
      },
      {
        kind: 'quiz',
        title: 'Tricks',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'How many cards are in one trick?',
            choices: ['Two', 'Four', 'Thirteen'],
            correct: 1,
          },
          {
            prompt: 'How many tricks are played in one hand?',
            choices: ['4', '13', '52'],
            correct: 1,
          },
          {
            prompt: 'Who leads the next trick?',
            choices: ['The player who won the last trick', 'Always the dealer', 'The player with the most points'],
            correct: 0,
          },
        ],
      },
    ],
  },
  {
    id: 'bridge-follow-suit',
    slug: 'bridge-following-suit',
    title: 'Following suit',
    audienceLevel: 'beginner',
    concepts: ['bridge'],
    steps: [
      {
        kind: 'explain',
        title: 'Play the suit that was led',
        body: 'The first card of a trick sets the suit. If you have a card of that suit, you must play one. If you have none, you may play any card you like. Without trump, the highest card of the suit that was led wins the trick, and Ace is the highest. A card of another suit can never win, however big it is. The game only lets you tap the cards you are allowed to play, and shows the others dimmed.',
        narration: 'Follow the suit that was led if you can. The highest of that suit wins.',
      },
      {
        kind: 'quiz',
        title: 'Follow the suit',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'Hearts are led and you hold a heart. What must you do?',
            choices: ['Play a heart', 'Play any card', 'Pass'],
            correct: 0,
          },
          {
            prompt: 'Hearts are led and you have no hearts. What may you play?',
            choices: ['Only a spade', 'Any card', 'Nothing, you skip'],
            correct: 1,
          },
          {
            prompt: 'With no trump, spades are led. A heart Ace is played. Does it win?',
            choices: ['Yes, an Ace always wins', 'No, only a spade can win', 'Only if it is played last'],
            correct: 1,
          },
        ],
      },
    ],
  },
  {
    id: 'bridge-trump',
    slug: 'bridge-trump-suit',
    title: 'Trump: the boss suit',
    audienceLevel: 'beginner',
    concepts: ['bridge'],
    steps: [
      {
        kind: 'explain',
        title: 'A suit that beats the rest',
        body: 'Before play, the declarer chooses a trump suit, usually the suit the two partners hold the most of together. If you cannot follow suit, you may play a trump, and any trump beats every card of the suit that was led. If more than one trump is played, the highest trump wins. If the partners have no long suit between them, the hand is played with no trump at all, and then the highest card of the suit led wins. The game suggests a trump suit, and the declarer may change it.',
        narration: 'Trump is the boss suit. A small trump beats a big card of any other suit.',
      },
      {
        kind: 'quiz',
        title: 'Trump',
        body: 'Time for a quick check!',
        questions: [
          {
            prompt: 'Diamonds are trump and clubs are led. You have no clubs. You play the two of diamonds. Who wins, if nobody else plays a trump?',
            choices: ['The highest club', 'You', 'The player to your left'],
            correct: 1,
          },
          {
            prompt: 'Two players play a trump in the same trick. Which wins?',
            choices: ['The first trump played', 'The highest trump', 'The lowest trump'],
            correct: 1,
          },
          {
            prompt: 'When is a hand played with no trump?',
            choices: [
              'Whenever the dealer says so',
              'When the partners have no long suit between them',
              'Only on the last trick',
            ],
            correct: 1,
          },
        ],
      },
    ],
  },
];
